import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import { firestore } from "../Firebase";
import Bg from "../Images/about.jpg";
import { Delete, CloudUpload, Add, Close, Image as ImageIcon } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { FaImages, FaCamera } from "react-icons/fa";

// Cloudinary configuration
const CLOUDINARY_CLOUD_NAME = "dzhmnzqfb";
const CLOUDINARY_UPLOAD_PRESET = "Ictaz_upload";

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSeverity, setFeedbackSeverity] = useState("success");
  const [selectedImage, setSelectedImage] = useState(null);

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": { borderColor: "rgba(0, 217, 255, 0.3)" },
      "&:hover fieldset": { borderColor: "rgba(0, 217, 255, 0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#00d9ff" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00d9ff" },
  };

  useEffect(() => {
    const loadImages = async () => {
      try {
        const snapshot = await firestore
          .collection("gallery")
          .orderBy("createdAt", "desc")
          .get();
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setImages(data);
      } catch (error) {
        console.error("Error fetching gallery: ", error);
      }
    };
    loadImages();
  }, []);

  const showFeedback = (message, severity = "success") => {
    setFeedbackMessage(message);
    setFeedbackSeverity(severity);
    setFeedbackOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showFeedback("Please select an image file", "error");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        showFeedback("Image size must be less than 10MB", "error");
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("folder", "gallery");

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: "POST", body: formData }
    );

    if (!response.ok) throw new Error("Failed to upload image");
    const data = await response.json();
    return data.secure_url;
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      showFeedback("Please select an image", "error");
      return;
    }
    setUploading(true);

    try {
      const imageUrl = await uploadImage(imageFile);

      const newImage = {
        caption,
        imageUrl,
        createdAt: new Date(),
      };

      const docRef = await firestore.collection("gallery").add(newImage);
      setImages([{ id: docRef.id, ...newImage }, ...images]);

      setOpen(false);
      setCaption("");
      setImageFile(null);
      setImagePreview("");
      showFeedback("Image added successfully!");
    } catch (error) {
      console.error("Error adding image:", error);
      showFeedback("Error adding image", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (image) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      await firestore.collection("gallery").doc(image.id).delete();
      setImages(images.filter((img) => img.id !== image.id));
      showFeedback("Image deleted successfully!");
    } catch (error) {
      console.error("Error deleting image:", error);
      showFeedback("Error deleting image", "error");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: "15px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaImages size={30} color="white" />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: 'white' }}>Gallery Management</Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>Upload and manage gallery images for the public website</Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Stats & Action */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, my: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Card sx={{ borderRadius: "15px", ...darkGlassStyle, px: 3, py: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ width: 45, height: 45, borderRadius: "12px", background: "rgba(0, 217, 255, 0.2)", border: "1px solid #00d9ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ImageIcon sx={{ color: "#00d9ff" }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: "#00d9ff" }}>{images.length}</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>Total Images</Typography>
                  </Box>
                </Box>
              </Card>
            </Box>
            <Button onClick={() => setOpen(true)} variant="contained" startIcon={<Add />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #00d9ff, #00b4d8)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #00b4d8, #0096c7)" } }}>
              Add New Image
            </Button>
          </Box>
        </motion.div>

        {/* Gallery Grid */}
        <Grid container spacing={3}>
          {images.length > 0 ? (
            images.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ y: -8 }}
                >
                  <Card
                    sx={{
                      borderRadius: "20px",
                      ...darkGlassStyle,
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        border: "1px solid rgba(0, 217, 255, 0.5)",
                        boxShadow: "0 15px 40px rgba(0, 217, 255, 0.2)",
                      },
                    }}
                  >
                    <Box sx={{ position: "relative", overflow: "hidden", cursor: "pointer" }} onClick={() => setSelectedImage(image)}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={image.imageUrl}
                        alt={image.caption || "Gallery image"}
                        sx={{ objectFit: "cover", transition: "transform 0.4s ease", "&:hover": { transform: "scale(1.1)" } }}
                      />
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "linear-gradient(to top, rgba(0,0,36,0.8) 0%, transparent 50%)", opacity: 0, transition: "opacity 0.3s ease", display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { opacity: 1 } }}>
                        <Box sx={{ width: 50, height: 50, borderRadius: "50%", background: "rgba(0, 217, 255, 0.3)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #00d9ff" }}>
                          <FaCamera size={20} color="#00d9ff" />
                        </Box>
                      </Box>
                    </Box>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {image.caption || "No caption"}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ borderTop: "1px solid rgba(0, 217, 255, 0.1)", px: 2, py: 1.5, justifyContent: "flex-end" }}>
                      <Button size="small" onClick={() => handleDelete(image)} startIcon={<Delete />}
                        sx={{ borderRadius: "8px", background: "rgba(239, 68, 68, 0.2)", border: "1px solid #ef4444", color: "#ef4444", "&:hover": { background: "rgba(239, 68, 68, 0.3)" } }}>
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
                <Card sx={{ borderRadius: "25px", ...darkGlassStyle, p: 6, textAlign: "center" }}>
                  <FaImages size={60} color="#00d9ff" style={{ marginBottom: 20 }} />
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: 'white' }}>No Images Yet</Typography>
                  <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)", mb: 3 }}>Start building your gallery by adding your first image</Typography>
                  <Button onClick={() => setOpen(true)} variant="contained" startIcon={<Add />}
                    sx={{ borderRadius: "12px", px: 4, py: 1.2, background: "linear-gradient(135deg, #00d9ff, #00b4d8)", fontWeight: 600 }}>
                    Add First Image
                  </Button>
                </Card>
              </motion.div>
            </Grid>
          )}
        </Grid>

        {/* Add Image Dialog */}
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaImages size={20} color="white" />
            </Box>
            Add New Image
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <form onSubmit={handleAddSubmit}>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                {/* Image Upload */}
                <Box sx={{ textAlign: "center" }}>
                  <input accept="image/*" style={{ display: "none" }} id="gallery-image-upload" type="file" onChange={handleImageChange} />
                  <label htmlFor="gallery-image-upload">
                    <Box sx={{ width: "100%", height: 200, border: "2px dashed rgba(0, 217, 255, 0.4)", borderRadius: "15px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", background: "rgba(0, 217, 255, 0.05)", transition: "all 0.3s ease", "&:hover": { borderColor: "#00d9ff", background: "rgba(0, 217, 255, 0.1)" } }}>
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <Box sx={{ textAlign: "center" }}>
                          <CloudUpload sx={{ fontSize: 50, color: "rgba(0, 217, 255, 0.5)", mb: 1 }} />
                          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>Click to upload image</Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>Max size: 10MB</Typography>
                        </Box>
                      )}
                    </Box>
                  </label>
                </Box>

                <TextField label="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} fullWidth multiline rows={2} sx={inputStyle} />

                <Button type="submit" variant="contained" fullWidth disabled={uploading || !imageFile}
                  sx={{ py: 1.5, borderRadius: "12px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", fontWeight: 600, fontSize: "1rem", "&:hover": { background: "linear-gradient(135deg, #00b4d8, #0096c7)" }, "&.Mui-disabled": { background: "rgba(0, 217, 255, 0.3)", color: "rgba(255,255,255,0.5)" } }}>
                  {uploading ? "Uploading..." : "Add Image"}
                </Button>
              </Stack>
            </form>
          </DialogContent>
        </Dialog>

        {/* Lightbox Dialog */}
        <AnimatePresence>
          {selectedImage && (
            <Dialog open={!!selectedImage} onClose={() => setSelectedImage(null)} maxWidth="lg"
              PaperProps={{ sx: { backgroundColor: "transparent", boxShadow: "none", overflow: "visible" } }}>
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.3 }}>
                <Box sx={{ position: "relative" }}>
                  <IconButton onClick={() => setSelectedImage(null)}
                    sx={{ position: "absolute", top: -50, right: -10, color: "white", backgroundColor: "rgba(0, 217, 255, 0.3)", backdropFilter: "blur(10px)", border: "1px solid rgba(0, 217, 255, 0.5)", "&:hover": { backgroundColor: "rgba(0, 217, 255, 0.5)" } }}>
                    <Close />
                  </IconButton>
                  <img src={selectedImage.imageUrl} alt={selectedImage.caption || "Gallery image"}
                    style={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: 16, boxShadow: "0 20px 60px rgba(0, 217, 255, 0.3)", border: "3px solid rgba(0, 217, 255, 0.3)" }} />
                  {selectedImage.caption && (
                    <Box sx={{ mt: 2, p: 2, borderRadius: "12px", background: "rgba(0, 0, 36, 0.9)", backdropFilter: "blur(10px)", border: "1px solid rgba(0, 217, 255, 0.3)" }}>
                      <Typography variant="body1" sx={{ color: "white", textAlign: "center", fontWeight: 500 }}>{selectedImage.caption}</Typography>
                    </Box>
                  )}
                </Box>
              </motion.div>
            </Dialog>
          )}
        </AnimatePresence>

        {/* Feedback Snackbar */}
        <Snackbar open={feedbackOpen} autoHideDuration={4000} onClose={() => setFeedbackOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert onClose={() => setFeedbackOpen(false)} severity={feedbackSeverity} sx={{ width: "100%", borderRadius: "12px" }}>{feedbackMessage}</Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AdminGallery;
